import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2, QrCode, X } from 'lucide-react';
import type {
  DeepLinkCampaign,
  DeepLinkCampaignAnalyticsDetail,
  DeepLinkSeededCampaignSummary,
} from '../../../models/MasterAdmin/deepLink';
import {
  fetchDeepLinkCampaign,
  fetchDeepLinkCampaignAnalyticsDetail,
  fetchDeepLinkCampaignQr,
} from '../../../services/MasterAdmin/deepLinkService';
import CopyLinkButton from './CopyLinkButton';
import {
  dlBtnSecondaryClass,
  dlCardClass,
  dlModalHeaderClass,
  dlModalOverlayClass,
  dlMutedTextClass,
} from './deepLinkUi';

export type SeededCampaignViewEntry = {
  label: string;
  campaign: DeepLinkSeededCampaignSummary;
};

type Props = {
  open: boolean;
  title: string;
  subtitle?: string;
  entries: SeededCampaignViewEntry[];
  onClose: () => void;
};

function eventTone(eventType: string) {
  if (eventType === 'NAVIGATION_SUCCESS') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200';
  }
  if (eventType === 'NAVIGATION_MISMATCH' || eventType === 'NAVIGATION_ERROR') {
    return 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200';
  }
  return 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200';
}

const DeepLinkSeededCampaignsViewModal: React.FC<Props> = ({
  open,
  title,
  subtitle,
  entries,
  onClose,
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [fullCampaign, setFullCampaign] = useState<DeepLinkCampaign | null>(null);
  const [detail, setDetail] = useState<DeepLinkCampaignAnalyticsDetail | null>(null);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const activeEntry = useMemo(
    () => entries.find((e) => e.campaign.id === activeId) || entries[0] || null,
    [entries, activeId]
  );

  useEffect(() => {
    if (!open) return;
    setActiveId(entries[0]?.campaign.id || null);
  }, [open, entries]);

  useEffect(() => {
    if (!open || !activeEntry?.campaign.id) {
      setFullCampaign(null);
      setDetail(null);
      setQrUrl(null);
      return;
    }
    const id = activeEntry.campaign.id;
    setLoading(true);
    void Promise.all([
      fetchDeepLinkCampaign(id).catch(() => null),
      fetchDeepLinkCampaignAnalyticsDetail(id).catch(() => null),
      fetchDeepLinkCampaignQr(id).catch(() => null),
    ])
      .then(([campaign, analytics, qr]) => {
        setFullCampaign(campaign);
        setDetail(analytics);
        setQrUrl(qr?.qrUrl || null);
      })
      .finally(() => setLoading(false));
  }, [open, activeEntry?.campaign.id]);

  if (!open || !entries.length) return null;

  const campaign = fullCampaign;
  const summary = detail?.summary;
  const shortUrl = campaign?.shortUrl || activeEntry?.campaign.shortUrl || '';
  const fullUrl = campaign?.fullUrl || activeEntry?.campaign.fullUrl || '';
  const errors =
    detail?.recentNavigationEvents?.filter(
      (e) => e.eventType === 'NAVIGATION_MISMATCH' || e.eventType === 'NAVIGATION_ERROR'
    ) || [];

  return (
    <div className={dlModalOverlayClass}>
      <div className="relative flex max-h-[92vh] w-full max-w-[96vw] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900 lg:max-w-5xl">
        <div className={dlModalHeaderClass}>
          <div className="min-w-0 pr-4">
            <h2 className="truncate text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
            {subtitle ? <p className="mt-0.5 truncate text-xs text-gray-500">{subtitle}</p> : null}
          </div>
          <button type="button" onClick={onClose} className="shrink-0 text-gray-500 dark:text-gray-400">
            <X className="h-5 w-5" />
          </button>
        </div>

        {entries.length > 1 ? (
          <div className="flex gap-1 border-b border-gray-200 px-4 pt-2 dark:border-gray-700 sm:px-6">
            {entries.map((entry) => {
              const active = entry.campaign.id === (activeEntry?.campaign.id || null);
              return (
                <button
                  key={entry.campaign.id}
                  type="button"
                  onClick={() => setActiveId(entry.campaign.id)}
                  className={`rounded-t-lg px-3 py-2 text-sm font-medium ${
                    active
                      ? 'border border-b-0 border-gray-200 bg-white text-[#128C7E] dark:border-gray-700 dark:bg-gray-900'
                      : 'text-gray-500 hover:text-gray-800 dark:text-gray-400'
                  }`}
                >
                  {entry.label}
                </button>
              );
            })}
          </div>
        ) : null}

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading campaign…
            </div>
          ) : (
            <>
              <div className="grid gap-3 lg:grid-cols-2">
                <div className={`${dlCardClass} p-3`}>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="font-medium text-gray-900 dark:text-white">Short link</p>
                    <CopyLinkButton text={shortUrl} label="Copy" />
                  </div>
                  <p className="break-all font-mono text-xs text-gray-700 dark:text-gray-300">
                    {shortUrl || '—'}
                  </p>
                </div>
                <div className={`${dlCardClass} p-3`}>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="font-medium text-gray-900 dark:text-white">Full deep link</p>
                    <CopyLinkButton text={fullUrl} label="Copy" />
                  </div>
                  <p className="break-all font-mono text-xs text-gray-700 dark:text-gray-300">
                    {fullUrl || '—'}
                  </p>
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-[200px_1fr]">
                <div className={`${dlCardClass} p-4 text-center`}>
                  <div className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
                    <QrCode className="h-4 w-4" /> QR
                  </div>
                  {qrUrl ? (
                    <img src={qrUrl} alt="QR" className="mx-auto h-36 w-36 rounded-lg" />
                  ) : (
                    <p className={dlMutedTextClass}>QR unavailable</p>
                  )}
                </div>

                <div className={`${dlCardClass} p-4`}>
                  <h3 className="mb-3 font-semibold text-gray-900 dark:text-white">
                    Link analysis{activeEntry ? ` · ${activeEntry.label}` : ''}
                  </h3>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {[
                      ['Clicks', summary?.totalClicks ?? campaign?.totalClicks ?? 0],
                      ['Web opens', summary?.webOpens ?? 0],
                      ['App opens', summary?.appOpens ?? summary?.appClicks ?? 0],
                      ['Installs', summary?.installs ?? campaign?.totalInstalls ?? 0],
                      ['Logins', summary?.logins ?? campaign?.totalLogins ?? 0],
                      ['Nav OK', summary?.navigationSuccesses ?? 0],
                      ['Nav mismatch', summary?.navigationMismatches ?? 0],
                      ['Nav errors', summary?.navigationErrors ?? 0],
                    ].map(([label, value]) => (
                      <div
                        key={String(label)}
                        className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800/80"
                      >
                        <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">{value}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4">
                    <p className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      Navigation errors / mismatches
                    </p>
                    {errors.length ? (
                      <div className="max-h-40 space-y-2 overflow-y-auto">
                        {errors.map((event) => (
                          <div
                            key={event.id}
                            className={`rounded-lg border p-3 text-xs ${eventTone(event.eventType)}`}
                          >
                            <span className="font-semibold">{event.eventType}</span>
                            {event.reason ? (
                              <p className="mt-1 opacity-90">{event.reason}</p>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className={dlMutedTextClass}>No navigation errors for this link</p>
                    )}
                  </div>
                </div>
              </div>

              {entries.length > 1 ? (
                <div className={`${dlCardClass} p-4`}>
                  <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
                    All links for this doctor
                  </h3>
                  <div className="space-y-3">
                    {entries.map((entry) => (
                      <div
                        key={entry.campaign.id}
                        className="rounded-lg border border-gray-100 p-3 dark:border-gray-700"
                      >
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                          {entry.label}
                        </p>
                        <div className="space-y-1">
                          <div className="flex min-w-0 items-center gap-2">
                            <span className="w-10 shrink-0 text-[11px] text-gray-400">Short</span>
                            <p className="min-w-0 flex-1 truncate font-mono text-[11px]">
                              {entry.campaign.shortUrl || '—'}
                            </p>
                            <CopyLinkButton text={entry.campaign.shortUrl} label="Copy" />
                          </div>
                          <div className="flex min-w-0 items-center gap-2">
                            <span className="w-10 shrink-0 text-[11px] text-gray-400">Full</span>
                            <p className="min-w-0 flex-1 truncate font-mono text-[11px]">
                              {entry.campaign.fullUrl || '—'}
                            </p>
                            <CopyLinkButton text={entry.campaign.fullUrl} label="Copy" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="flex justify-end border-t border-gray-200 px-4 py-3 dark:border-gray-700 sm:px-6">
          <button type="button" onClick={onClose} className={dlBtnSecondaryClass}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeepLinkSeededCampaignsViewModal;
